import React from 'react';
import { Instagram, MapPin, Music, Play } from 'lucide-react';
import SpotifyEmbed from './SpotifyEmbed';

const TrackList = ({ artist, onPlay }) => (
  <div className="mb-8">
    <p className="text-silver-400 text-xs uppercase tracking-[0.25em] mb-4">Top Tracks</p>
    <div className="space-y-1">
      {artist.tracks.map((track, idx) => (
        <button
          key={track.title}
          onClick={() => onPlay(artist, track)}
          className="w-full flex items-center gap-4 px-3 py-2.5 rounded-lg text-left group/track hover:bg-silver-400/10 transition-colors duration-300"
          data-testid={`${artist.slug}-track-${idx}`}
        >
          <span className="w-8 h-8 rounded-full bg-silver-400/10 group-hover/track:bg-silver-400 flex items-center justify-center flex-shrink-0 transition-colors duration-300">
            <Play size={13} className="text-silver-400 group-hover/track:text-black ml-0.5 transition-colors duration-300" />
          </span>
          <span className="flex-1 text-white/80 group-hover/track:text-white text-sm font-medium truncate transition-colors duration-300">
            {track.title}
          </span>
          <span className="text-white/40 text-xs">{track.duration}</span>
        </button>
      ))}
    </div>
  </div>
);

const ArtistSocials = ({ artist }) => (
  <div className="flex flex-wrap gap-4 mb-8">
    {artist.social.spotify && (
      <a
        href={artist.social.spotify}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-2 px-6 py-3 bg-silver-400 hover:bg-silver-300 text-black font-bold text-xs uppercase tracking-wider rounded-lg transition-all duration-300 hover:scale-105"
        data-testid={`${artist.slug}-spotify-link`}
      >
        <Music size={16} />
        Spotify
      </a>
    )}
    {artist.social.instagram && (
      <a
        href={artist.social.instagram}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-2 px-6 py-3 border border-silver-400/40 hover:border-silver-400 text-white font-bold text-xs uppercase tracking-wider rounded-lg transition-all duration-300 hover:scale-105"
        data-testid={`${artist.slug}-instagram-link`}
      >
        <Instagram size={16} />
        Instagram
      </a>
    )}
  </div>
);

const ArtistInfo = ({ artist, onPlayTrack }) => (
  <div className="p-8 md:p-12 flex flex-col justify-center">
    {artist.location && (
      <div className="flex items-center gap-2 text-silver-400 text-xs uppercase tracking-[0.25em] mb-4">
        <MapPin size={14} />
        {artist.location}
      </div>
    )}
    <h2 className="text-white text-3xl md:text-5xl font-black leading-tight mb-3" data-testid={`${artist.slug}-name`}>
      {artist.name}
    </h2>
    <p className="text-silver-400 text-sm uppercase tracking-wider mb-6">{artist.genre}</p>
    <p className="text-white/80 text-base md:text-lg leading-relaxed mb-8" data-testid={`${artist.slug}-bio`}>
      {artist.bio}
    </p>

    <ArtistSocials artist={artist} />

    {artist.tracks?.length > 0 && <TrackList artist={artist} onPlay={onPlayTrack} />}

    {artist.social.spotify && (
      <SpotifyEmbed
        url={artist.social.spotify}
        title={`${artist.name} on Spotify`}
        artwork={artist.image}
        height={352}
      />
    )}
  </div>
);

export const RosterArtistCard = ({ artist, onPlayTrack }) => (
  <div
    id={artist.slug}
    className="bg-gradient-to-br from-silver-900/30 to-silver-900/10 border-2 border-silver-400/30 rounded-2xl overflow-hidden mb-16 scroll-mt-28"
    data-testid={`artist-card-${artist.slug}`}
  >
    <div className="grid md:grid-cols-2">
      <div className="relative min-h-[400px] md:min-h-[560px]">
        <img
          src={artist.image}
          alt={artist.name}
          className="absolute inset-0 w-full h-full object-cover"
          data-testid={`${artist.slug}-photo`}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent md:bg-gradient-to-r" />
      </div>
      <ArtistInfo artist={artist} onPlayTrack={onPlayTrack} />
    </div>
  </div>
);
